import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTransactions, useDeleteTransaction } from '@/hooks/api'
import { TRANSACTIONS_PER_PAGE } from './TransactionsPage.static'
import type { TransactionType, TransactionCategory, FinancialTransaction } from '@/types'
import type { GetTransactionsParams } from '@/services/api/financial/financial.static'

export function useTransactionsPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<TransactionType | ''>('')
  const [categoryFilter, setCategoryFilter] = useState<TransactionCategory | ''>('')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [editTargetTransaction, setEditTargetTransaction] = useState<FinancialTransaction | null>(
    null
  )
  const { t } = useTranslation('financials')
  const deleteTransactionMutation = useDeleteTransaction()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const params: GetTransactionsParams = {
    page,
    limit: TRANSACTIONS_PER_PAGE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(typeFilter && { transactionType: typeFilter }),
    ...(categoryFilter && { category: categoryFilter }),
  }

  const { data, isLoading } = useTransactions(params)

  const transactions = data?.transactions ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / TRANSACTIONS_PER_PAGE)
  const isEmpty = !isLoading && transactions.length === 0

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value === 'all' ? '' : (value as TransactionType))
    setPage(1)
  }

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value === 'all' ? '' : (value as TransactionCategory))
    setPage(1)
  }

  const handleDeleteTransaction = () => {
    if (!deleteTargetId) return
    deleteTransactionMutation.mutate(deleteTargetId, {
      onSettled: () => setDeleteTargetId(null),
    })
  }

  return {
    t,
    transactions,
    total,
    page,
    totalPages,
    search,
    typeFilter,
    categoryFilter,
    addDialogOpen,
    deleteTargetId,
    editTargetTransaction,
    isLoading,
    isEmpty,
    setSearch,
    setPage,
    setAddDialogOpen,
    setDeleteTargetId,
    setEditTargetTransaction,
    handleTypeFilterChange,
    handleCategoryFilterChange,
    handleDeleteTransaction,
  }
}
